import { ChangeDetectionStrategy, Component, effect, ElementRef, HostListener, input, OnInit, output, signal, inject } from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { NavSection, NavItem } from '../../../models/sidebar.model';

/**
 * Provides the navigation sidebar for the application.
 * The sidebar displays grouped navigation sections and supports
 * expandable Users and Logs submenus. It also handles responsive
 * behavior by collapsing the sidebar on smaller screens and closing
 * submenu flyouts when the user clicks outside the sidebar.
 */
@Component({
  selector: 'zba-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  /**
   * Provides Angular router functionality for reading the current
   * route and determining active navigation items.
   */
  private readonly router = inject(Router);

  /**
   * Provides access to the sidebar's host DOM element.
   * Used to determine whether a document click occurred outside
   * the sidebar when a submenu flyout is open.
   */
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Signal input that determines whether the sidebar is collapsed.
   * When collapsed, submenu items are displayed in collapsed-mode
   * behavior and expanded submenu states are automatically cleared.
   */
  readonly collapsed = input<boolean>(false);

  /**
   * Signal output emitted when the sidebar needs to change
   * its collapsed state.
   * This is primarily used to notify the parent layout component
   * when the sidebar should automatically collapse on smaller screens.
   */
  readonly collapsedChange = output<boolean>();

  /**
   * Reactive state indicating whether the Users submenu is expanded.
   */
  readonly isUsersExpanded = signal<boolean>(false);

  /**
   * Reactive state indicating whether the Logs submenu is expanded.
   */
  readonly isLogsExpanded = signal<boolean>(false);

  /**
   * Effect that closes all expanded submenus whenever the sidebar
   * enters collapsed mode.
   * This keeps submenu state synchronized with the collapsed sidebar
   * presentation.
   */
  private readonly closeSubmenuWhenCollapsed = effect(() => {
    if (this.collapsed()) {
      this.isUsersExpanded.set(false);
      this.isLogsExpanded.set(false);
    }
  });

  /**
   * Navigation sections and their corresponding menu items.
   * The navigation is grouped into Analytics and Management sections.
   * Management items can contain nested child routes.
   */
  readonly navSections: readonly NavSection[] = [
    {
      sectionLabel: 'Analytics',
      items: [
        { label: 'Signup Analytics', route: '/analytics/signup', icon: 'bi-person-plus', title: 'Signup Analytics' },
        { label: 'Trial Conversion Analytics', route: '/analytics/trial-conversion', icon: 'bi-graph-up-arrow', title: 'Trial Conversion Analytics' }
      ]
    },
    {
      sectionLabel: 'Management',
      items: [
        {
          label: 'Users',
          icon: 'bi-people',
          title: 'Users',
          children: [
            { label: 'Role', route: '/users/roles', icon: 'bi-shield-check', title: 'Role' },
            { label: 'Staff', route: '/users/staff', icon: 'bi-person-badge', title: 'Staff' },
          ]
        },
        {
          label: 'Logs',
          icon: 'bi-journal-text',
          title: 'Logs',
          children: [
            { label: 'Event Error Logs', route: '/logs/event-error-logs', icon: 'bi-exclamation-triangle', title: 'Event Error Logs' },
          ]
        }
      ]
    }
  ];

  /**
   * Initializes the sidebar component.
   * Checks the current route and automatically expands the relevant
   * submenu when the sidebar is not collapsed.
   */
  ngOnInit(): void {
    this.checkAndExpandMenus();
  }

  /**
   * Checks whether the current route belongs to one of the sidebar
   * submenus and automatically expands the corresponding submenu.
   * The Users submenu is expanded for routes beginning with `/users/`,
   * while the Logs submenu is expanded for routes beginning with `/logs/`.
   */
  private checkAndExpandMenus(): void {
    const currentUrl = this.router.url;
    if (!this.collapsed() && currentUrl.startsWith('/users/')) {
      this.isUsersExpanded.set(true);
    }
    if (!this.collapsed() && currentUrl.startsWith('/logs/')) {
      this.isLogsExpanded.set(true);
    }
  }

  /**
   * Determines whether a parent navigation item has an active child route.
   * The method checks each child route against the current router URL
   * and returns `true` when a matching active child is found.
   * @param item Parent navigation item whose children should be checked.
   * @returns `true` when at least one child route is active; otherwise, `false`.
   */
  isParentWithActiveChild(item: NavItem): boolean {
    if (!item.children) {
      return false;
    }
    return item.children.some(child => {
      return child.route && this.router.url.startsWith(child.route);
    });
  }

  /**
   * Toggles the expansion state of the requested submenu.
   * The Users and Logs menu items each control their corresponding
   * submenu signal.
   * @param item Navigation item whose submenu should be toggled.
   */
  toggleMenu(item: NavItem): void {
    if (item.label === 'Users') {
      this.isUsersExpanded.update(state => !state);
    }
    if (item.label === 'Logs') {
      this.isLogsExpanded.update(state => !state);
    }
  }

  /**
   * Returns whether the requested submenu is currently expanded.
   * @param item Navigation item whose expansion state should be checked.
   * @returns `true` when the corresponding submenu is expanded;
   * otherwise, `false`.
   */
  isMenuExpanded(item: NavItem): boolean {
    if (item.label === 'Users') {
      return this.isUsersExpanded();
    }
    if (item.label === 'Logs') {
      return this.isLogsExpanded();
    }
    return false;
  }

  /**
   * Handles clicks anywhere in the document.
   * When the sidebar is collapsed and a submenu flyout is open,
   * clicking outside the sidebar closes the expanded submenus.
   * @param event Mouse event generated by the document click.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if ((!this.isUsersExpanded() && !this.isLogsExpanded()) || !this.collapsed()) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isUsersExpanded.set(false);
      this.isLogsExpanded.set(false);
    }
  }

  /**
   * Handles navigation item clicks.
   * When the sidebar is already collapsed, the Users submenu is closed.
   * On mobile and tablet screen widths, when the sidebar is expanded,
   * the method emits a request to collapse the sidebar after navigation.
   */
  onNavItemClick(): void {
    if (this.collapsed()) {
      this.isUsersExpanded.set(false);
    }
    if (typeof window !== 'undefined' && window.innerWidth <= 1024 && !this.collapsed()) {
      this.collapsedChange.emit(true);
    }
  }
}